import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Printer, CalendarClock, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { fmtDate } from "@/lib/util";
import { Loader, Pill, VehNo } from "@/components/common";

const thisMonth = () => new Date().toISOString().slice(0, 7);
const monthLabel = (m) => new Date(`${m}-01T00:00:00`).toLocaleDateString("ar-KW", { month: "long", year: "numeric" });

function ReportRow({ d, i }) {
  return (
    <tr className="border-b" data-testid={`monthly-row-${d.id}`}>
      <td className="p-2">{i + 1}</td>
      <td className="p-2 font-bold">{d.name}</td>
      <td className="p-2"><VehNo adm={d.vehicle_admin_number} plate={d.vehicle_plate} /></td>
      <td className="p-2">{d.driver ? <>{d.driver.full_name} <span className="ltr text-slate-500">{d.driver.serial}</span></> : "—"}</td>
      <td className="p-2 ltr text-right">{d.driver?.phone || "—"}</td>
      <td className="p-2 ltr text-right">{d.number || "—"}</td>
      <td className="p-2 font-bold">{fmtDate(d.expiry_date)}</td>
      <td className="p-2">{d.days_left < 0 ? <Pill color="red">منتهي</Pill> : <Pill color="orange">باقي {d.days_left} يوم</Pill>}</td>
    </tr>
  );
}

export default function MonthlyDocsReport() {
  const [month, setMonth] = useState(thisMonth());
  const [list, setList] = useState(null);
  const [company, setCompany] = useState("");
  useEffect(() => { api.get("/settings").then((r) => setCompany(r.data.company_name)).catch(() => {}); }, []);
  useEffect(() => {
    setList(null);
    api.get("/documents/monthly", { params: { month } }).then((r) => setList(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [month]);
  const count = (t) => (list || []).filter((d) => d.doc_type === t).length;
  return (
    <div data-testid="monthly-docs-report">
      <div className="no-print flex flex-wrap items-end gap-3 mb-5">
        <Link to="/admin/documents" data-testid="monthly-back" className="btn btn-sm btn-ghost"><ArrowRight className="w-4 h-4" />المستندات</Link>
        <label className="block"><span className="font-bold block mb-1 text-sm">الشهر</span>
          <input data-testid="monthly-month" type="month" className="field field-sm" value={month} onChange={(e) => e.target.value && setMonth(e.target.value)} /></label>
        <button data-testid="monthly-print" onClick={() => window.print()} disabled={!list} className="btn btn-md btn-dark"><Printer className="w-5 h-5" />طباعة</button>
      </div>
      <div className="card p-5 print:shadow-none print:border-0">
        <div className="flex justify-between items-start border-b pb-3 mb-4">
          <div>
            <h1 className="text-2xl font-black font-head flex items-center gap-2"><CalendarClock className="w-6 h-6 text-green-700" />الاستمارات والتأمينات المنتهية في {monthLabel(month)}</h1>
            <p className="text-slate-500 text-sm mt-1">{company}</p>
          </div>
          <div className="text-sm text-left" data-testid="monthly-summary">
            <div>استمارات: <b>{count("registration")}</b></div>
            <div>تأمينات: <b>{count("insurance")}</b></div>
            <div className="text-slate-500">تاريخ الطباعة: {fmtDate(new Date().toISOString())}</div>
          </div>
        </div>
        {!list ? <Loader /> : list.length === 0 ? <p className="text-center text-slate-500 py-8" data-testid="monthly-empty">لا توجد استمارات أو تأمينات تنتهي في هذا الشهر</p> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-right" data-testid="monthly-table">
              <thead><tr className="bg-slate-100">{["#", "المستند", "المركبة", "السائق", "هاتف السائق", "رقم المستند", "تاريخ الانتهاء", "الحالة"].map((h) => <th key={h} className="p-2 font-bold whitespace-nowrap">{h}</th>)}</tr></thead>
              <tbody>{list.map((d, i) => <ReportRow key={d.id} d={d} i={i} />)}</tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
